/**
 * Mobile Layout - Responsive layout wrapper optimized for mobile-first design
 * Features: Safe areas, keyboard handling, orientation support, gesture navigation
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { usePWA } from '@/hooks/usePWA';
import MobileNavigation from '@/components/mobile/MobileNavigation';
import { PWAStatusBar } from '@/components/pwa/PWAPrompts';

interface MobileLayoutProps {
  children: React.ReactNode;
  className?: string;
  showNavigation?: boolean;
  showStatusBar?: boolean;
  allowPullToRefresh?: boolean;
  onRefresh?: () => Promise<void>;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

interface ViewportInfo {
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  isKeyboardOpen: boolean;
  safeAreaInsets: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export const MobileLayout: React.FC<MobileLayoutProps> = ({
  children,
  className,
  showNavigation = true,
  showStatusBar = true,
  allowPullToRefresh = true,
  onRefresh,
  header,
  footer
}) => {
  const { isStandalone, refresh } = usePWA();
  const [viewport, setViewport] = useState<ViewportInfo>({
    width: window.innerWidth,
    height: window.innerHeight,
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
    isKeyboardOpen: false,
    safeAreaInsets: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }
  });

  const layoutRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const initialViewportHeight = useRef(window.innerHeight);

  // Handle viewport changes
  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const orientation = width > height ? 'landscape' : 'portrait';
      
      // Detect keyboard on mobile (viewport height reduction)
      const heightDifference = initialViewportHeight.current - height;
      const isKeyboardOpen = heightDifference > 150; // 150px threshold

      // Get safe area insets from CSS environment variables
      const computedStyle = getComputedStyle(document.documentElement);
      const safeAreaInsets = {
        top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0'),
        right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right') || '0'),
        bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0'),
        left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left') || '0')
      };

      setViewport({
        width,
        height,
        orientation,
        isKeyboardOpen,
        safeAreaInsets
      });
    };

    // Update immediately
    updateViewport();

    // Listen for viewport changes
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    // Listen for visual viewport changes (keyboard)
    if ('visualViewport' in window) {
      const visualViewport = window.visualViewport as any;
      visualViewport.addEventListener('resize', updateViewport);
      
      return () => {
        window.removeEventListener('resize', updateViewport);
        window.removeEventListener('orientationchange', updateViewport);
        visualViewport.removeEventListener('resize', updateViewport);
      };
    }

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  // Handle pull-to-refresh
  useEffect(() => {
    if (!allowPullToRefresh || !contentRef.current) return;

    let startY = 0;
    let currentY = 0;
    let isPulling = false;
    let isRefreshing = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (contentRef.current!.scrollTop !== 0) return;
      startY = e.touches[0].clientY;
      isPulling = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling || isRefreshing) return;

      currentY = e.touches[0].clientY;
      const pullDistance = currentY - startY;

      if (pullDistance > 0 && contentRef.current!.scrollTop === 0) {
        e.preventDefault();
        
        // Add visual feedback for pull distance
        const pullProgress = Math.min(pullDistance / 100, 1);
        contentRef.current!.style.transform = `translateY(${Math.min(pullDistance * 0.5, 50)}px)`;
        contentRef.current!.style.opacity = `${1 - pullProgress * 0.2}`;
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling || isRefreshing) return;

      const pullDistance = currentY - startY;
      isPulling = false;

      if (pullDistance > 100) {
        isRefreshing = true;
        
        try {
          if (onRefresh) {
            await onRefresh();
          } else {
            await refresh();
          }
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          isRefreshing = false;
        }
      }

      // Reset transform
      if (contentRef.current) {
        contentRef.current.style.transform = '';
        contentRef.current.style.opacity = '';
      }
    };

    const content = contentRef.current;
    content.addEventListener('touchstart', handleTouchStart, { passive: false });
    content.addEventListener('touchmove', handleTouchMove, { passive: false });
    content.addEventListener('touchend', handleTouchEnd);

    return () => {
      content.removeEventListener('touchstart', handleTouchStart);
      content.removeEventListener('touchmove', handleTouchMove);
      content.removeEventListener('touchend', handleTouchEnd);
    };
  }, [allowPullToRefresh, onRefresh, refresh]);

  // Set CSS custom properties for safe areas
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--safe-area-inset-top', `${viewport.safeAreaInsets.top}px`);
    root.style.setProperty('--safe-area-inset-right', `${viewport.safeAreaInsets.right}px`);
    root.style.setProperty('--safe-area-inset-bottom', `${viewport.safeAreaInsets.bottom}px`);
    root.style.setProperty('--safe-area-inset-left', `${viewport.safeAreaInsets.left}px`);
    root.style.setProperty('--viewport-width', `${viewport.width}px`);
    root.style.setProperty('--viewport-height', `${viewport.height}px`);
  }, [viewport]);

  // Handle keyboard appearance
  useEffect(() => {
    if (viewport.isKeyboardOpen) {
      document.body.classList.add('keyboard-open');
    } else {
      document.body.classList.remove('keyboard-open');
    }
  }, [viewport.isKeyboardOpen]);

  const containerStyle = {
    paddingTop: `max(${viewport.safeAreaInsets.top}px, env(safe-area-inset-top))`,
    paddingRight: `max(${viewport.safeAreaInsets.right}px, env(safe-area-inset-right))`,
    paddingBottom: showNavigation ? 
      `max(${Math.max(viewport.safeAreaInsets.bottom, 80)}px, calc(80px + env(safe-area-inset-bottom)))` :
      `max(${viewport.safeAreaInsets.bottom}px, env(safe-area-inset-bottom))`,
    paddingLeft: `max(${viewport.safeAreaInsets.left}px, env(safe-area-inset-left))`
  };

  return (
    <div
      ref={layoutRef}
      className={cn(
        'min-h-screen bg-gray-50',
        'flex flex-col',
        'touch-manipulation',
        viewport.orientation === 'landscape' && 'landscape',
        viewport.isKeyboardOpen && 'keyboard-open',
        isStandalone && 'standalone',
        className
      )}
      style={containerStyle}
    >
      {/* PWA Status Bar */}
      {showStatusBar && <PWAStatusBar />}

      {/* Header */}
      <AnimatePresence>
        {header && (
          <motion.header
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-20"
          >
            {header}
          </motion.header>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main
        ref={contentRef}
        className={cn(
          'flex-1 overflow-y-auto',
          'transition-transform duration-200 ease-out',
          allowPullToRefresh && 'overscroll-behavior-y-contain'
        )}
        style={{
          height: viewport.isKeyboardOpen ? 
            `${viewport.height - (header ? 64 : 0) - (footer ? 64 : 0)}px` : 
            'auto'
        }}
      >
        <div className="min-h-full">
          {children}
        </div>
      </main>

      {/* Footer */}
      <AnimatePresence>
        {footer && (
          <motion.footer
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="flex-shrink-0 bg-white border-t border-gray-200 shadow-sm z-20"
          >
            {footer}
          </motion.footer>
        )}
      </AnimatePresence>

      {/* Mobile Navigation */}
      {showNavigation && (
        <MobileNavigation className="md:hidden" />
      )}

      {/* Orientation change indicator */}
      <AnimatePresence>
        {viewport.orientation === 'landscape' && viewport.width < 768 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 md:hidden"
          >
            <div className="bg-white rounded-xl p-6 m-4 text-center max-w-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <motion.div
                  animate={{ rotate: 90 }}
                  className="w-6 h-6 border-2 border-blue-600 rounded"
                />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Rotate Device
              </h3>
              <p className="text-sm text-gray-600">
                For the best experience, please use portrait orientation.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Safe Area Hook
export const useSafeArea = () => {
  const [safeArea, setSafeArea] = useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  });

  useEffect(() => {
    const updateSafeArea = () => {
      const computedStyle = getComputedStyle(document.documentElement);
      setSafeArea({
        top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0'),
        right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right') || '0'),
        bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0'),
        left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left') || '0')
      });
    };

    updateSafeArea();
    window.addEventListener('resize', updateSafeArea);
    
    return () => window.removeEventListener('resize', updateSafeArea);
  }, []);

  return safeArea;
};

// Viewport Hook
export const useViewport = () => {
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait' as 'portrait' | 'landscape',
    isKeyboardOpen: false
  });

  useEffect(() => {
    const updateViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const orientation = width > height ? 'landscape' : 'portrait';
      const isKeyboardOpen = height < window.screen.height * 0.75;

      setViewport({
        width,
        height,
        orientation,
        isKeyboardOpen
      });
    };

    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  return viewport;
};

export default MobileLayout;